import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  InputAdornment,
  Table,
  Box,
  Modal,
  TableBody,
  TableCell,
  CircularProgress,
  TableHead,
  AppBar,
  Toolbar,
  Paper,
  TableRow,
  IconButton,
  Typography,
  Grid,
  Tooltip
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import base64 from "base64-js";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function DueDateDetails() {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [fineDetails, setFineDetails] = useState([]);
  const [selectedFineDetails, setSelectedFineDetails] = useState({ dueDateId: null, monthName: '' });
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [authorization, setAuthorization] = useState(null);
  const navigate = useNavigate();
  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const fetchFineDetails = async () => {
    try {
      setIsLoading(true); // Start loading
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Fine/GetFine`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        console.error('Record Not Found');
        // alert('Record Not Found');
        setSnackbarMessage('Record Not Found');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");

        return;
      }
      setFineDetails(data);
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    } finally {
      setIsLoading(false); // Stop loading
    }
  };


  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData[0]);
      } else {
        console.error("Authorization failed");
        // alert("Authorization failed");
        setSnackbarMessage('Authorization failed');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
        setAuthorization({ isPermission: 0 });
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setAuthorization({ isPermission: 0 });
    }
  };

  const handleShow = (dueDateId, monthName) => {
    if (authorization?.uDelete === 1) {
      setSelectedFineDetails({ dueDateId, monthName });
      setShowModal(true);
    } else {
      // alert("You are not authorized to delete");
      setSnackbarMessage('You are not authorized to delete');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
    }
  };

  const handleClose = () => {
    setSelectedFineDetails({ dueDateId: null, monthName: '' });
    setShowModal(false);
  };

  const handleEdit = (fineId) => {
    if (authorization?.uModify === 1) {
      navigate(`/editfine/${fineId}`);
    } else {
      // alert("You are not authorized to edit");
      setSnackbarMessage('You are not authorized to edit');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
    }
  };

  const handleDelete = async (fineId) => {
    if (authorization?.uDelete !== 1) {
      // alert("You are not authorized to delete");
      setSnackbarMessage('You are not authorized to delete');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Fine/Id?Id=${fineId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });
      if (response.ok) {
        setShowModal(false);
        setLoadingBarProgress(100);
        setSnackbarMessage('Fine deleted successfully!');
        setOpenSnackbar(true);
        fetchFineDetails();
      } else {
        console.error("Delete failed");
        setLoadingBarProgress(0);
        // alert("Failed to delete fine");
        setSnackbarMessage('Failed to delete fine');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  useEffect(() => {
    if (formId) {
      Authorizer();
      if (authorization?.uRead === 1) {
        fetchFineDetails();
      }
    } else {
      setAuthorization({ isPermission: 0 });
    }
  }, [formId, authorization?.uRead]);

  useEffect(() => {
    // Filter fine details based on search term
    // You can implement this logic if needed
  }, [searchTerm, fineDetails]);

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  const filteredData = fineDetails
    .filter((fine) => fine.monthName.toLowerCase().includes(searchTerm.toLowerCase()));

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );



  return (
    <Container>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}></Grid>
        <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
          <Toolbar>
            <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
              Fine Details
            </Typography>
          </Toolbar>
        </AppBar>
        <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
          <div style={{ display: 'flex', justifyContent: "space-between", alignItems: 'center', margin: "10px auto" }}>
            <TextField
              type="text"
              id="search"
              placeholder="Search"
              style={{ width: "300px" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Tooltip title={authorization.uWrite === 1 ? "" : "You are not authorized to add"} arrow>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    width: "120px",
                    height: "50px", // match TextField height
                    padding: "10px 0", // vertical padding similar to TextField
                  }}
                  onClick={() => navigate('/addfine')}
                  disabled={authorization.uWrite !== 1}
                >
                  <b>ADD</b>
                </Button>

              </span>
            </Tooltip>
          </div>

          <Grid item xs={12}>
            <Table
              sx={{
                minWidth: 650,
                border: '1px solid #ccc',
                '& .MuiTableCell-root': {
                  borderRight: '1px solid #ccc',
                  fontSize: '1rem',
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ textAlign: 'center' }}><strong>Fee Type</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Fine For</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Start Date</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>End Date</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Fine Per Day Date</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((fine) => (
                    <TableRow key={fine.dueDateId}>
                      <TableCell style={{ textAlign: 'center' }}>{fine.chargeType}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.monthName}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.startDate}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.endDate}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.finePerDay}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        <Tooltip title={authorization.uModify === 1 ? "" : "You are not authorized to edit"} arrow>
                          <span>
                            <Button
                              variant="contained"
                              color="warning"
                              spacing={2}
                              onClick={() => handleEdit(fine.dueDateId)}
                              startIcon={<EditIcon />}
                              disabled={authorization.uModify !== 1}
                            >
                              <b>EDIT</b>
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={authorization.uDelete === 1 ? "" : "You are not authorized to delete"} arrow>
                          <span>
                            <Button
                              variant="contained"
                              color="error"
                              style={{ marginLeft: "10px" }}
                              onClick={() => handleShow(fine.dueDateId, fine.monthName)}
                              startIcon={<DeleteIcon />}
                              disabled={authorization.uDelete !== 1}
                            >
                              <b>DELETE</b>
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No Fine Details Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
            {filteredData.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <TextField
                  select
                  label="Rows per page"
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{ width: 150 }}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {rowsPerPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </TextField>

                <Box>
                  <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                    Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => handleChangePage(null, page - 1)}
                    disabled={page === 1}
                    sx={{ mr: 1 }}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleChangePage(null, page + 1)}
                    disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

          </Grid>
        </Paper>
      </Grid>


      <Modal open={showModal} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete fine for <strong>{selectedFineDetails.monthName}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(selectedFineDetails.dueDateId)}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
}

export default DueDateDetails;
